// Runtime (jsdom) coverage for the post-purchase account-activation redirect logic.
//
// The production code is an inline <script> in
// app/views/journeys/_account_activation_redirect.html.haml (it must run inline,
// before the parser-blocking OneTrust script, so a hung CDN can't trap the
// customer - which is why it isn't an importable module). This spec is a faithful
// behavioural MIRROR of that snippet: the Ruby view spec
// (spec/views/journeys/account_activation_redirect_view_shared_examples.rb) pins the
// partial to the same constructs (poll, longstop, idempotency, never-wrap), and this
// spec proves those constructs actually behave. Keep the two in step.
//
// `navigate` stands in for `window.location.href = url` so jsdom doesn't attempt a
// real navigation; the behaviour otherwise matches the partial.
const isComplete = (entry) => Boolean(entry) && entry.event === "purchase_events_complete";

const armAccountActivationRedirect = ({ redirectUrl, longstopMs, pollIntervalMs, navigate }) => {
  let hasRedirected = false;
  let pollId;

  const doRedirect = () => {
    if (hasRedirected) {
      return;
    }
    hasRedirected = true;
    clearInterval(pollId);
    navigate(redirectUrl);
  };

  window.dataLayer = window.dataLayer || [];
  pollId = setInterval(() => {
    if ((window.dataLayer || []).some(isComplete)) {
      doRedirect();
    }
  }, pollIntervalMs);

  setTimeout(doRedirect, longstopMs);
};

describe("account-activation redirect", () => {
  const redirectUrl = "https://account.example.com/create-password?u=abc&t=xyz";
  const longstopMs = 5000;
  const pollIntervalMs = 100;
  let navigate;

  beforeEach(() => {
    jest.useFakeTimers();
    navigate = jest.fn();
    delete window.dataLayer;
  });

  afterEach(() => {
    jest.clearAllTimers();
    jest.useRealTimers();
  });

  it("redirects when purchase_events_complete arrives via a GTM-replaced push", () => {
    armAccountActivationRedirect({ redirectUrl, longstopMs, pollIntervalMs, navigate });

    // Simulate GTM taking over dataLayer.push after gtm.js loads. The completion
    // event is pushed through the REPLACED push - a wrapper on the original push
    // would miss it, but polling the array does not.
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = (...args) => originalPush(...args);
    window.dataLayer.push({ event: "purchase_events_complete" });

    expect(navigate).not.toHaveBeenCalled(); // not until the next poll tick
    jest.advanceTimersByTime(pollIntervalMs);

    expect(navigate).toHaveBeenCalledTimes(1);
    expect(navigate).toHaveBeenCalledWith(redirectUrl);
  });

  it("redirects via the longstop when the completion signal never arrives", () => {
    armAccountActivationRedirect({ redirectUrl, longstopMs, pollIntervalMs, navigate });

    jest.advanceTimersByTime(longstopMs - 1);
    expect(navigate).not.toHaveBeenCalled();

    jest.advanceTimersByTime(1);
    expect(navigate).toHaveBeenCalledTimes(1);
    expect(navigate).toHaveBeenCalledWith(redirectUrl);
  });

  it("redirects at most once when both the signal and the longstop fire", () => {
    armAccountActivationRedirect({ redirectUrl, longstopMs, pollIntervalMs, navigate });

    window.dataLayer.push({ event: "purchase_events_complete" });
    jest.advanceTimersByTime(pollIntervalMs); // signal fires the redirect
    jest.advanceTimersByTime(longstopMs); // longstop would fire, but is idempotent

    expect(navigate).toHaveBeenCalledTimes(1);
  });

  it("ignores unrelated dataLayer events and waits for the longstop", () => {
    armAccountActivationRedirect({ redirectUrl, longstopMs, pollIntervalMs, navigate });

    window.dataLayer.push({ event: "purchase_new" });
    window.dataLayer.push({ event: "gtm.load" });
    jest.advanceTimersByTime(pollIntervalMs * 5);
    expect(navigate).not.toHaveBeenCalled();

    jest.advanceTimersByTime(longstopMs);
    expect(navigate).toHaveBeenCalledTimes(1);
  });
});
